import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import styled from 'styled-components'
import ContractApi from '../../apis/contract'
import { useAuthProvider } from '../../contexts/auth'
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from '../../pages/contract/components/accordion'

const FixedAccordionOnlySp = styled(StyledAccordion)`
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    margin-top: -${(p) => p.theme.space.xl} !important;
    margin-left: -${(p) => p.theme.space.l} !important;
    margin-right: -${(p) => p.theme.space.l} !important;
    padding-top: -${(p) => p.theme.space.xl} !important;
    padding-left: -${(p) => p.theme.space.l} !important;
    padding-right: -${(p) => p.theme.space.l} !important;
    &.Mui-expanded {
      margin-top: -${(p) => p.theme.space.xl} !important;
      margin-left: -${(p) => p.theme.space.l} !important;
      margin-right: -${(p) => p.theme.space.l} !important;
      padding-top: -${(p) => p.theme.space.xl} !important;
      padding-left: -${(p) => p.theme.space.l} !important;
      padding-right: -${(p) => p.theme.space.l} !important;
    }
  }
`

const UnorderedList = styled.ul`
  margin: 0;
  padding-left: 30px;
  padding-right: 30px;
`
const ListItem = styled.li`
  font-size: ${(p) => p.theme.font.size.m};
  margin-bottom: 10px;
`

const SummaryContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px 16px;
  width: 100%;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    flex-direction: column;
  }
`

const SummaryText = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${(p) => p.theme.font.size.m};
  gap: 4px;
`

const SummaryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const SummaryTitle = styled.h4`
  margin: 0;
  font-size: ${(p) => p.theme.font.size.l};
  font-weight: 700;
  color: ${(p) => p.theme.FONT_COLOR};
`

const HelpIconWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const HelpIcon = styled.span`
  width: 24px;
  height: 24px;
  background: url('/image/help.svg') center/contain no-repeat;
  cursor: help;
`

const HelpTooltip = styled.span`
  position: absolute;
  left: 0;
  top: 24px;
  min-width: 220px;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid ${(p) => p.theme.BORDER};
  border-radius: 6px;
  color: ${(p) => p.theme.FONT_COLOR};
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
  z-index: 1;

  ${HelpIconWrapper}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`

const ActionButton = styled.button`
  border: none;
  background: transparent;
  color: ${(p) => p.theme.FONT_COLOR_GRAY};
  cursor: pointer;
  padding: 4px 8px;
  font-size: 12px;
  align-self: flex-end;

  &:hover {
    opacity: 0.7;
  }
`

const NextActionArea = (props: {
  isContract: boolean
  id: string
  suumoUnderAverageListPvPercent?: number | null
  suumoUnderAverageTransitionRate?: number | null
  refresh: () => void
}) => {
  const { claims } = useAuthProvider()

  const isAgent = claims?.role === 'agent'
  const onHidden = async () => {
    if (props.isContract) {
      await ContractApi.confirmContractNotification(claims?.role!, props.id)
      props.refresh()
      return
    }
    await ContractApi.confirmFeatureNotification(claims?.role!, props.id)

    props.refresh()
  }
  if (
    !props.suumoUnderAverageListPvPercent &&
    !props.suumoUnderAverageTransitionRate
  ) {
    return <></>
  }
  return (
    <FixedAccordionOnlySp defaultExpanded background="secondary" radius="none">
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SummaryContent>
          <SummaryText>
            <SummaryTitleRow>
              <SummaryTitle>お知らせ</SummaryTitle>
              <HelpIconWrapper>
                <HelpIcon />
                <HelpTooltip>
                  SUUMOの掲載で一覧PVなどがエリア平均値を下回った場合に表示されます。
                </HelpTooltip>
              </HelpIconWrapper>
            </SummaryTitleRow>
          </SummaryText>
        </SummaryContent>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <UnorderedList>
          {props.suumoUnderAverageListPvPercent && (
            <ListItem>
              一覧PVが{props.suumoUnderAverageListPvPercent}
              %平均より下回りました。
              <br />
              {isAgent &&
                '対象の物件が検索結果に表示されていません。suumoの検索の仕組み上、500万刻みで検索が行われるので、それを意識して値段を変更してみましょう。'}
              {!isAgent &&
                '問い合わせも少ないようであれば、周辺の成約・売り出し事例をご確認いただき、適切な売り出し方法をエージェントとご相談ください。'}
            </ListItem>
          )}
          {props.suumoUnderAverageTransitionRate && (
            <ListItem>
              {isAgent && (
                <>
                  一覧から物件詳細画面への遷移率が
                  {props.suumoUnderAverageTransitionRate}%平均を下回りました。
                  <br />
                  検索結果には表示されていますが、他の物件に閲覧が流れている可能性があります。
                  同条件の他物件と比較しつつ、1-3枚目の写真の入れ替え、一覧キャッチ(有料オプション)、価格変更を検討してみましょう。
                </>
              )}
              {!isAgent && (
                <>
                  検索結果上でのクリック率が
                  {props.suumoUnderAverageTransitionRate}%平均より下回りました。
                  <br />
                  売り出し方法や価格をエージェントと見直すことで改善できる可能性があります。
                </>
              )}
            </ListItem>
          )}
        </UnorderedList>
        <ActionButton type="button" onClick={onHidden}>
          表示しない
        </ActionButton>
      </StyledAccordionDetails>
    </FixedAccordionOnlySp>
  )
}

export default NextActionArea
